import { alpha } from '@mui/material/styles';
import { colors } from './ai-theme';

/**
 * Centralized Clerk appearance configuration
 * Matches the Prospero MUI theme (Outfit + Plus Jakarta Sans, purple primary, rounded inputs)
 */
export const clerkAppearance = {
  variables: {
    colorPrimary: colors.primary.main,
    colorDanger: colors.accent.coral,
    colorSuccess: colors.accent.success,
    colorWarning: colors.accent.amber,
    colorTextOnPrimaryBackground: colors.primary.contrastText,
    colorTextSecondary: colors.text.secondary,
    colorText: colors.text.primary,
    colorBackground: colors.background.paper,
    colorInputBackground: colors.background.paper,
    colorInputText: colors.text.primary,
    borderRadius: '10px',
    fontFamily: '"Outfit Variable", "Inter", system-ui, sans-serif',
    fontSize: '15px',
    fontWeight: { normal: 400, medium: 500, semibold: 600, bold: 700 },
    spacingUnit: '16px',
  },
  elements: {
    rootBox: {
      width: '100%',
    },
    cardBox: {
      boxShadow: 'none',
      border: 'none',
      width: '100%',
    },
    card: {
      boxShadow: 'none',
      border: 'none',
      borderRadius: '16px',
      background: colors.background.paper,
    },
    header: {
      display: 'none',
    },
    socialButtonsBlockButton: {
      border: `1px solid ${colors.border.light}`,
      borderRadius: '10px',
      fontWeight: 500,
      fontSize: '0.9rem',
      color: colors.text.primary,
      transition: 'all 0.2s ease',
      '&:hover': {
        backgroundColor: colors.states.hover,
        borderColor: colors.border.main,
      },
    },
    socialButtonsBlockButtonText: {
      fontWeight: 500,
      color: colors.text.primary,
    },
    // Divider
    dividerLine: {
      borderColor: colors.border.light,
    },
    dividerText: {
      color: colors.text.muted,
      fontSize: '0.8rem',
      textTransform: 'uppercase' as const,
      letterSpacing: '0.05em',
    },
    formFieldLabel: {
      fontWeight: 500,
      color: colors.text.primary,
      fontSize: '0.85rem',
    },
    formFieldInput: {
      borderRadius: '10px',
      border: `1px solid ${colors.border.light}`,
      fontSize: '0.95rem',
      color: colors.text.primary,
      transition: 'all 0.2s ease',
      '&:hover': {
        borderColor: colors.border.main,
      },
      '&:focus': {
        borderColor: colors.primary.main,
        boxShadow: `0 0 0 3px ${alpha(colors.primary.main, 0.1)}`,
        borderWidth: '2px',
      },
    },
    formFieldAction: {
      color: colors.primary.main,
      fontWeight: 500,
      '&:hover': {
        color: colors.primary.dark,
      },
    },
    formButtonPrimary: {
      background: `linear-gradient(135deg, ${colors.primary.main}, ${colors.primary.light})`,
      borderRadius: '10px',
      fontWeight: 600,
      fontSize: '0.95rem',
      textTransform: 'none' as const,
      boxShadow: 'none',
      transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        background: `linear-gradient(135deg, ${colors.primary.light}, ${colors.primary.main})`,
        boxShadow: 'none',
      },
      '&:active': {
        transform: 'scale(0.98)',
      },
    },
    // Footer links
    footerActionLink: {
      color: colors.primary.main,
      fontWeight: 500,
      '&:hover': {
        color: colors.primary.dark,
      },
    },
    footerActionText: {
      color: colors.text.secondary,
    },
    // Footer branding
    footer: {
      '& + div': {
        background: 'transparent',
      },
    },
    footerPagesLink: {
      color: colors.text.muted,
    },
    // Badge / tag styles
    badge: {
      borderRadius: '20px',
      fontWeight: 500,
    },
    // Identifier preview
    identityPreview: {
      borderRadius: '10px',
      border: `1px solid ${colors.border.light}`,
    },
    identityPreviewText: {
      color: colors.text.primary,
    },
    identityPreviewEditButton: {
      color: colors.primary.main,
    },
    // OTP input
    otpCodeFieldInput: {
      borderRadius: '10px',
      border: `1px solid ${colors.border.light}`,
      '&:focus': {
        borderColor: colors.primary.main,
        boxShadow: `0 0 0 3px ${alpha(colors.primary.main, 0.1)}`,
      },
    },
    // Alternative methods
    alternativeMethodsBlockButton: {
      border: `1px solid ${colors.border.light}`,
      borderRadius: '10px',
      fontWeight: 500,
      color: colors.text.primary,
      '&:hover': {
        backgroundColor: colors.states.hover,
        borderColor: colors.border.main,
      },
    },
    // Alert
    alert: {
      borderRadius: '10px',
    },
    // User button (popover)
    userButtonPopoverCard: {
      borderRadius: '12px',
      border: `1px solid ${colors.border.light}`,
      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.12), 0 4px 10px rgba(0, 0, 0, 0.08)',
    },
    userButtonPopoverActionButton: {
      borderRadius: '6px',
      '&:hover': {
        backgroundColor: colors.states.hover,
      },
    },
    userButtonPopoverActionButtonText: {
      color: colors.text.primary,
    },
    // User profile
    navbarButton: {
      borderRadius: '6px',
      '&:hover': {
        backgroundColor: colors.states.hover,
      },
    },
    profileSectionTitle: {
      fontFamily: '"Plus Jakarta Sans Variable", "Outfit Variable", system-ui, sans-serif',
      fontWeight: 600,
    },
  },
};
